import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

/**
 * Top progress bar for page navigation.
 *
 * Moving between admin pages used to give no feedback at all: the sidebar
 * highlight changed, then the screen sat on the old content until the new
 * page's query resolved. On a slow connection that reads as a dead click.
 *
 * The bar is driven by two signals rather than a timer, so it reflects what is
 * actually happening:
 *
 *   1. a route change — shown immediately, because React renders the new page
 *      before its data exists
 *   2. any in-flight RTK Query request — read straight from the store, so the
 *      bar stays up until the page's data actually lands, not for a guessed
 *      number of milliseconds
 *
 * It fills to 90% and waits there. Completion is the one thing we cannot
 * predict, so it never fakes reaching 100% — it snaps there only when the work
 * is genuinely finished.
 */
export default function RouteProgress() {
    const { pathname } = useLocation();
    const [visible, setVisible] = useState(false);
    const [width, setWidth] = useState(0);
    const timers = useRef([]);

    // True while ANY api slice has a request in flight. Reading the store keeps
    // this honest across every page without each one having to opt in.
    const busy = useSelector((state) =>
        Object.values(state).some(
            (slice) =>
                slice &&
                typeof slice === "object" &&
                slice.queries &&
                Object.values(slice.queries).some((q) => q?.status === "pending")
        )
    );

    const clearTimers = () => {
        timers.current.forEach(clearTimeout);
        timers.current = [];
    };

    // Start on navigation.
    useEffect(() => {
        clearTimers();
        setVisible(true);
        setWidth(15);
        timers.current.push(setTimeout(() => setWidth(55), 120));
        timers.current.push(setTimeout(() => setWidth(90), 450));
        return clearTimers;
    }, [pathname]);

    // Finish once nothing is loading. The short delay avoids a flicker when a
    // page fires several queries in sequence — without it the bar would complete
    // and restart between them.
    useEffect(() => {
        if (!visible || busy) return undefined;
        const done = setTimeout(() => {
            setWidth(100);
            setTimeout(() => {
                setVisible(false);
                setWidth(0);
            }, 250);
        }, 180);
        return () => clearTimeout(done);
    }, [busy, visible]);

    if (!visible) return null;

    return (
        <div
            className="fixed inset-x-0 top-0 z-[2000] h-[3px] bg-transparent pointer-events-none"
            role="progressbar"
            aria-label="Loading"
        >
            <div
                className="h-full bg-primary shadow-[0_0_8px_rgba(194,206,46,0.7)] transition-[width] duration-300 ease-out"
                style={{ width: `${width}%` }}
            />
        </div>
    );
}
